import { NavLink, useLocation } from 'react-router-dom';
import { Newspaper, Bell, UserRound, UsersRound } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { QlashIcon } from '@/components/common/QlashIcon';
import { useAppSelector } from '@/store/hooks';
import { selectUnreadInboxCount } from '@/store/engagement.slice';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { cn } from '@/lib/cn';

interface TabDef {
  to: string;
  key: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  hub?: boolean;
}

// 5 tabs total. The Tournaments tab was removed — Sit&Go + community
// tournaments now live on the Hub. Layout: News · QSocials(Friends) · [Hub] ·
// Inbox · Profile. The Inbox bell carries an unread dot when there's something
// new. Rightmost is the Profile/account hub (gear). Hub stays centred (3rd).
// Icons only — no text labels (the `key` is the accessible name via aria-label).
//
// L'ultima voce è il PROFILO, non le impostazioni: la schermata dice chi sei e
// cosa scegli, e le impostazioni sono una delle due porte che apre. L'icona
// passa da ingranaggio a persona — un ingranaggio prometteva configurazione, e
// chi cercava il proprio profilo non lo apriva.
const tabs: readonly TabDef[] = [
  { to: '/news', key: 'news', Icon: Newspaper },
  { to: '/friends', key: 'friends', Icon: UsersRound },
  { to: '/', key: 'hub', Icon: QlashIcon, hub: true },
  { to: '/inbox', key: 'inbox', Icon: Bell },
  { to: '/profile', key: 'profile', Icon: UserRound },
] as const;

/** Quale voce è attiva, con la stessa regola che NavLink applica a sé. */
function activeKey(pathname: string): string | undefined {
  return tabs.find((t) => (t.to === '/' ? pathname === '/' : pathname.startsWith(t.to)))?.key;
}

export function TabBar() {
  const unreadInbox = useAppSelector(selectUnreadInboxCount);
  const { pathname } = useLocation();
  const active = activeKey(pathname);

  /**
   * La pastiglia che insegue la voce attiva — stesso meccanismo del segmentato
   * e delle schede sottolineate, quindi lo stesso rimbalzo. Un movimento
   * diverso qui direbbe che è un'altra cosa, e non lo è.
   *
   * Sulla Hub NON esiste: quella voce ha già la sua pastiglia piena, che sporge
   * e si accende. Mettergliene una seconda sotto sarebbe due indicatori per lo
   * stesso stato.
   */
  const { containerRef, rect, ready } = useSlidingIndicator(active ?? '');
  const onHub = active === 'hub';
  /** Sul Profilo la barra è la cinghia dello zaino: cuoio invece che teal. */
  const onPack = active === 'profile';

  return (
    <nav
      className={cn(
        // Angoli superiori arrotondati: la barra smette di essere un bordo
        // dello schermo e diventa una superficie appoggiata sopra il contenuto,
        // in famiglia con le card. Solo in alto — sotto tocca il bordo del
        // dispositivo, e arrotondare li' lascerebbe passare due spicchi di
        // sfondo negli angoli.
        'sticky bottom-0 z-10 rounded-t-2xl',
        // OPACA, non `bg-bg/95` con `backdrop-blur`.
        // Il velo sfocato prendeva la texture a scaglie che sta su <body> con
        // `background-attachment: fixed` e ne stampava una copia SFOCATA dentro
        // la barra — mentre appena fuori, negli spicchi lasciati liberi dagli
        // angoli, la stessa texture restava NITIDA. Due versioni dello stesso
        // motivo a un pixel di distanza: e' quello che si leggeva come uno
        // stacco sbagliato, non l'arrotondamento.
        // Il 95% inoltre rendeva la barra un filo diversa da ogni altra
        // superficie dell'app, che sono tutte piene.
        // ⚠️ UNA sola espressione, non due classi che si accavallano. Prima
        // c'erano `onPack && 'bg-pack'` piu' avanti e `'bg-bg'` qui: entrambe
        // finivano nella lista, e a parita' di proprieta' vince l'ULTIMA — la
        // barra restava verde acqua sul Profilo mentre il codice diceva marrone.
        //
        // Sul Profilo la barra e' la cinghia dello zaino: una cinghia attaccata
        // a una superficie verde acqua sarebbe appiccicata a un'interfaccia.
        onPack ? 'bg-pack' : 'bg-bg',
        // Bordo CONTINUO attorno alla forma, non `border-t`.
        // Un bordo solo superiore segue la curva degli angoli e poi si
        // interrompe: restano due tratti di fianco senza segno, e la riga in
        // alto sembra avere due buchi alle estremita'. L'anello interno di
        // `card-edge` gira tutt'intorno, quindi la barra ha un contorno solo —
        // ed e' lo stesso segno di ogni altra card (ADR-0038, punto 2).
        // Sul cuoio il contorno e' la CUCITURA, non il filo teal delle card.
        onPack ? '[box-shadow:inset_0_0_0_1px_var(--color-pack-stitch)]' : 'card-edge',
        // Inset di sicurezza PIENO + 8px, e l'inset lo assorbe QUI dentro: così
        // il fondo della barra arriva al bordo fisico dello schermo (niente
        // striscia di texture sotto) mentre le icone restano sopra la barra
        // gesti, dove i tocchi non competono con quelli di sistema.
        //
        // Il MAGGIORE fra le due fonti, non una sola: `env()` è lo standard del
        // browser e `--tg-viewport-safe-area-inset-bottom` è quello che dichiara
        // Telegram, e non sempre coincidono — dentro la WebView capita che uno
        // dei due valga zero. Prendendo il massimo la barra è corretta comunque,
        // e su desktop, dove valgono zero entrambi, restano i soli 8px.
        'pb-[calc(max(env(safe-area-inset-bottom,0px),var(--tg-viewport-safe-area-inset-bottom,0px))+0.5rem)]',
      )}
    >
      {/* Il `flex` scende su questo contenitore invece di stare sul <nav>: la
          pastiglia si posiziona in assoluto rispetto a lui, e serve un elemento
          `relative` che NON sia la barra — sulla barra il posizionamento
          partirebbe da dentro il bordo e l'inset di sicurezza in fondo. */}
      <div ref={containerRef} className="relative flex">
        {/* Sotto le icone (`-z-10`), non dietro un fondo: la pastiglia deve
            passare SOTTO la pallina della Hub quando la attraversa, altrimenti
            per un istante le copre la base.
            `opacity` e non smontaggio: sulla Hub sparisce ma resta dov'era, così
            tornando su una voce laterale riparte da lì invece di comparire di
            colpo a destinazione. */}
        {rect && (
          <span
            aria-hidden
            className={cn(
              'absolute -z-10 rounded-full',
              // Sul cuoio non è una pastiglia PIENA ma una CUCITURA: contorno
              // tratteggiato, niente fondo. Su un oggetto di pelle il modo di
              // marcare una zona non è colorarla, è cucirci attorno — e il
              // tratteggio è il punto del filo.
              // Funziona anche perché il vuoto lascia vedere il cuoio: una
              // pastiglia piena avrebbe coperto proprio il materiale che
              // dà identità a questa schermata.
              onPack ? 'border-2 border-dashed border-pack-fg/55 bg-transparent' : 'bg-brand/15',
            )}
            style={{
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
              opacity: onHub ? 0 : 1,
              // `ready` sopprime la transizione al primo disegno: senza,
              // all'avvio la pastiglia partirebbe dall'angolo e volerebbe fino
              // alla voce attiva, raccontando un cambio mai avvenuto.
              transition: ready
                ? `left 320ms ${BOUNCE}, width 320ms ${BOUNCE}, opacity 160ms ease-out`
                : 'none',
            }}
          />
        )}

        {tabs.map(({ to, key, Icon, hub }) => (
          <NavLink
            key={to}
            data-tour={`tab-${key}`}
            to={to}
            end={to === '/'}
            aria-label={key}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center py-2 transition-colors',
                // Sul cuoio le icone spente prendono il tono chiaro dello zaino:
                // il teal spento su marrone si legge sporco, per la stessa
                // ragione per cui il grigio si legge sporco sul teal.
                // Sul cuoio anche l'ATTIVA non è teal: dentro lo zaino l'accento
                // dell'app non ha voce. La voce attiva si distingue perché è
                // chiara e ha la pastiglia sotto, non perché è di un altro colore.
                isActive
                  ? onPack
                    ? 'text-pack-fg'
                    : 'text-brand'
                  : onPack
                    ? 'text-pack-muted'
                    : 'text-fg-muted',
              )
            }
          >
            {({ isActive }) =>
              hub ? (
                <span
                  className={cn(
                    '-mt-5 flex h-[3.25rem] w-[3.25rem] translate-y-[3px] items-center justify-center rounded-full transition-colors',
                    // Da inattiva la stella prende `text-fg-muted`, come ogni
                    // altra icona spenta della barra. Prima era in accento
                    // sempre, e faceva leggere la Hub come selezionata da
                    // qualunque schermata: nella barra l'accento deve dire UNA
                    // cosa sola — dove sei — e se lo dice anche altrove smette di
                    // dirlo del tutto.
                    // La pastiglia resta comunque riconoscibile: è più grande,
                    // sporge, e ha il suo pozzo scuro. Non serviva il colore.
                    isActive
                      ? 'bg-brand text-brand-fg shadow-[0_4px_16px_rgba(0,231,223,0.35)]'
                      : onPack
                        ? // Sul cuoio la pallina diventa un BOTTONE di metallo.
                          // Due dettagli che la rendono un oggetto invece di un
                          // cerchio grigio: una ghiera più chiara tutt'intorno
                          // (`ring`), e una luce interna in alto con un'ombra in
                          // basso — il modo in cui si legge una cosa bombata,
                          // illuminata da sopra come tutto il resto.
                          'bg-pack-stud text-pack-deep ring-2 ring-[var(--color-pack-stitch)] [box-shadow:inset_0_2px_3px_rgba(255,255,255,0.45),inset_0_-3px_4px_rgba(0,0,0,0.30)]'
                        : 'bg-[#002423] text-fg-muted',
                  )}
                >
                  <Icon className="h-[3.1rem] w-[3.1rem]" />
                </span>
              ) : (
                // Lo span ESTERNO è quello che la pastiglia misura, e il suo
                // padding è la forma della pastiglia: allargando qui si allarga
                // lei. Misurare direttamente l'icona darebbe un cerchietto di
                // 24px, misurare il link darebbe una fascia larga un quinto della
                // barra — la forma giusta non è nessuna delle due.
                // `data-active` NON va sulla Hub: quando è lei l'attiva nessun
                // elemento lo porta, la misura si ferma e la pastiglia resta
                // dov'era invece di saltare al centro.
                <span
                  data-active={isActive ? 'true' : undefined}
                  className="flex items-center justify-center rounded-full px-5 py-1.5"
                >
                  <span className="relative">
                    <Icon aria-hidden className="h-6 w-6" />
                    {key === 'inbox' && unreadInbox > 0 && (
                      <span
                        aria-hidden
                        className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-brand ring-2 ring-bg"
                      />
                    )}
                  </span>
                </span>
              )
            }
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
